/**
 * Reactive below-`md` viewport read (plan 07 — mobile-responsive retrofit).
 *
 * Yap runs as an iframe inside a hub pane; the pane contract (conventions §4)
 * guarantees that below `md` the iframe receives the FULL viewport, so this
 * media query answers honestly to the device. Most of the retrofit is pure CSS
 * (`src/styles/mobile.css`, one boundary at 767.98px per conventions §1); this
 * hook exists only for surfaces whose STRUCTURE changes below `md` — the T3
 * viewer mode dropping the Ribbon and OptionsBar (drafting authoring) and
 * showing the §7 handoff notice on phones. `matchMedia` listeners keep it
 * reactive to resizes and orientation changes without a reload.
 */
import { useSyncExternalStore } from "react";

/** The one boundary from the conventions doc, shared with mobile.css. */
const MOBILE_MAX_WIDTH = 767.98;

export function isMobileViewport(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

/** Reactive read — flips on resize and on orientation change, no reload needed. */
export function useMobileViewport(): boolean {
  return useSyncExternalStore(
    subscribeMobileViewport,
    isMobileViewport,
    () => false,
  );
}

function subscribeMobileViewport(listener: () => void): () => void {
  const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
  const onChange = () => listener();
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
